import type {
  DOMConversionMap,
  DOMConversionOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import { $applyNodeReplacement, DecoratorNode, DOMExportOutput } from "lexical";
import { F } from "ramda";
import React, { JSX, Suspense } from "react";
import { ImageComponent } from "./ImageComponent";

export type SerializedImageNode = Spread<
  {
    src: string;
    altText: string;
    width?: number;
    height?: number;
  },
  SerializedLexicalNode
>;

interface CreateImageNodePayload {
  src: string;
  altText: string;
  width?: number;
  height?: number;
}

function $convertImageElement(domNode: HTMLElement): null | DOMConversionOutput {
  if (domNode instanceof HTMLImageElement) {
    const { src, alt } = domNode;
    if (src === "") {
      return null;
    }
    const width = domNode.getAttribute("width");
    const height = domNode.getAttribute("height");
    const node = $createImageNode({
      altText: alt,
      height: height !== null ? parseInt(height, 10) : undefined,
      src,
      width: width !== null ? parseInt(width, 10) : undefined,
    });
    return { node };
  }

  return null;
}

/**
 * Decorator node that embeds an image inline as a base64 data URL. The full
 * `data:image/...;base64,...` URL is kept in the `src` so it serializes straight
 * into the editor's HTML output and round-trips on input. When selected in an
 * editable editor the image can be resized; the chosen width/height are persisted
 * and exported as `<img width height>` attributes.
 */
export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width: "inherit" | number;
  __height: "inherit" | number;

  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__height, node.__key);
  }

  constructor(src: string, altText: string, width?: "inherit" | number, height?: "inherit" | number, key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width ?? "inherit";
    this.__height = height ?? "inherit";
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    return $createImageNode({
      altText: serializedNode.altText,
      height: serializedNode.height,
      src: serializedNode.src,
      width: serializedNode.width,
    }).updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      altText: this.__altText,
      height: this.__height === "inherit" ? undefined : this.__height,
      src: this.__src,
      width: this.__width === "inherit" ? undefined : this.__width,
    };
  }

  createDOM(): HTMLElement {
    const element = document.createElement("span");
    element.className = "editor-image";
    return element;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("img");
    element.setAttribute("src", this.__src);
    element.setAttribute("alt", this.__altText);
    if (this.__width !== "inherit") {
      element.setAttribute("width", this.__width.toString());
    }
    if (this.__height !== "inherit") {
      element.setAttribute("height", this.__height.toString());
    }
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: $convertImageElement,
        priority: 0,
      }),
    };
  }

  updateDOM = F;

  getTextContent(): string {
    // Images carry no text; keep them out of the MaxLengthPlugin character count.
    return "";
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  setWidthAndHeight(width: "inherit" | number, height: "inherit" | number): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <ImageComponent
          altText={this.__altText}
          height={this.__height}
          nodeKey={this.getKey()}
          src={this.__src}
          width={this.__width}
        />
      </Suspense>
    );
  }
}

export function $createImageNode({ src, altText, width, height }: CreateImageNodePayload): ImageNode {
  return $applyNodeReplacement(new ImageNode(src, altText, width, height));
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
